import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';

import { AppDialog } from './AppDialog';

import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from './confirmContext';

/**
 * 확인 대화상자 프로바이더 — `const confirm = useConfirm(); if (await confirm({...}))` 패턴.
 * window.confirm 대체(브라우저 모달 금지). 앱 루트에 1개 마운트.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AppDialog
        open={options !== null}
        onOpenChange={(open) => !open && settle(false)}
        title={options?.title ?? ''}
        description={options?.description}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => settle(false)}>
              {options?.cancelLabel ?? '취소'}
            </Button>
            <Button
              variant={options?.destructive ? 'destructive' : 'default'}
              onClick={() => settle(true)}
            >
              {options?.confirmLabel ?? '확인'}
            </Button>
          </>
        }
      />
    </ConfirmContext.Provider>
  );
}
